import React from "react";
import { Box, Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";

export default function GraficoDatos({
    titulosColumnas,
    titulosColumnas2,
    datosTabla,
    tipoGrafico = "bar",
    tituloX,
    tituloY,
    ajustarRangoY = false,
}) {
    if (!datosTabla || datosTabla.length === 0) {
        return (
            <Box sx={{ p: 4, textAlign: "center", color: "#757575" }}>
                <Typography>No hay datos para mostrar</Typography>
            </Box>
        );
    }

    // Verificar si hay dos filas de encabezado
    const tieneDobleEncabezado = titulosColumnas2 && titulosColumnas2.length > 0;

    // Preparar datos para el gráfico
    const labels = datosTabla.map((fila) => fila[0]?.toString() || "");

    // El título del eje X combina ambos encabezados si existen
    let ejeXTitulo = tituloX || titulosColumnas[0] || "";
    if (tieneDobleEncabezado && titulosColumnas2[0]) {
        // Extraer solo la parte relevante de cada título
        const titulo1 = titulosColumnas[0]?.replace(/\s*\(.*\)/, "") || "";
        const titulo2Match = titulosColumnas2[0]?.match(/ALT\.?\s*NUBE[S]?/i);
        const titulo2 = titulo2Match ? "ALT. NUBES" : titulosColumnas2[0]?.replace(/HORA\s*\(UTC\)\s*\/?\s*/i, "").replace(/\s*\(.*\)/, "") || "";
        ejeXTitulo = `${titulo1} / ${titulo2}`;
    }

    // El título del eje Y se infiere o usa "Valor"
    const ejeYTitulo = tituloY || "Valor";

    // Obtener las series de datos (columnas excepto la primera que son los labels)
    const series = [];
    const numColumnas = titulosColumnas.length;

    for (let col = 1; col < numColumnas; col++) {
        const data = datosTabla.map((fila) => {
            const valor = fila[col];
            if (valor === undefined || valor === null || valor === "") return 0;
            // Convertir string con coma a número
            const numStr = valor.toString().replace(",", ".");
            return parseFloat(numStr) || 0;
        });

        // Combinar etiquetas de ambos encabezados si existen
        let seriesLabel = titulosColumnas[col] || `Serie ${col}`;
        if (tieneDobleEncabezado && titulosColumnas2[col]) {
            seriesLabel = `${titulosColumnas[col]} / ${titulosColumnas2[col]}`;
        }

        series.push({
            data,
            label: seriesLabel,
        });
    }

    // Colores para las series (extendido para soportar más intervalos)
    const colors = [
        "#1a8a9e",
        "#3baec7",
        "#7cb342",
        "#ffa726",
        "#ef5350",
        "#ab47bc",
        "#26a69a",
        "#ec407a",
        "#5c6bc0",
        "#66bb6a",
        "#ffca28",
        "#8d6e63",
        "#42a5f5",
        "#78909c",
        "#d4e157",
        "#ff7043",
    ];

    const chartHeight = 400;

    // Mostrar todas las series de datos sin límite
    const seriesToShow = series;

    // Calcular rango Y ajustado si es necesario
    let yAxisConfig = {
        label: ejeYTitulo,
        labelStyle: {
            fontSize: 12,
            fontWeight: 600,
            fill: "#424242",
        },
    };

    if (ajustarRangoY) {
        // Obtener todos los valores de todas las series
        const allValues = seriesToShow.flatMap((s) => s.data).filter((v) => v !== 0 && v !== null);
        if (allValues.length > 0) {
            const minVal = Math.min(...allValues);
            const maxVal = Math.max(...allValues);
            // Agregar un margen del 5% arriba y abajo
            const margin = (maxVal - minVal) * 0.05;
            yAxisConfig.min = Math.floor(minVal - margin);
            yAxisConfig.max = Math.ceil(maxVal + margin);
        }
    }

    if (tipoGrafico === "line") {
        return (
            <Box sx={{ width: "100%", height: chartHeight }}>
                <LineChart
                    xAxis={[
                        {
                            data: labels,
                            scaleType: "band",
                            label: ejeXTitulo,
                            tickLabelStyle: {
                                fontSize: 11,
                            },
                            labelStyle: {
                                fontSize: 12,
                                fontWeight: 600,
                                fill: "#424242",
                            },
                        },
                    ]}
                    yAxis={[yAxisConfig]}
                    series={seriesToShow.map((s, i) => ({
                        ...s,
                        color: colors[i % colors.length],
                        curve: "linear",
                    }))}
                    height={chartHeight}
                    margin={{ left: 70, right: 20, top: 40, bottom: 60 }}
                    slotProps={{
                        legend: {
                            direction: "row",
                            position: { vertical: "top", horizontal: "middle" },
                            padding: 0,
                            itemMarkWidth: 10,
                            itemMarkHeight: 10,
                            markGap: 5,
                            itemGap: 15,
                            labelStyle: {
                                fontSize: 11,
                            },
                        },
                    }}
                />
            </Box>
        );
    }

    return (
        <Box sx={{ width: "100%", height: chartHeight }}>
            <BarChart
                xAxis={[
                    {
                        data: labels,
                        scaleType: "band",
                        label: ejeXTitulo,
                        tickLabelStyle: {
                            fontSize: 11,
                            angle: labels.length > 12 ? 45 : 0,
                            textAnchor: labels.length > 12 ? "start" : "middle",
                        },
                        labelStyle: {
                            fontSize: 12,
                            fontWeight: 600,
                            fill: "#424242",
                        },
                    },
                ]}
                yAxis={[yAxisConfig]}
                series={seriesToShow.map((s, i) => ({
                    ...s,
                    color: colors[i % colors.length],
                }))}
                height={chartHeight}
                margin={{
                    left: 70,
                    right: 20,
                    top: 40,
                    bottom: labels.length > 12 ? 90 : 60
                }}
                slotProps={{
                    legend: {
                        direction: "row",
                        position: { vertical: "top", horizontal: "middle" },
                        padding: 0,
                        itemMarkWidth: 10,
                        itemMarkHeight: 10,
                        markGap: 5,
                        itemGap: 15,
                        labelStyle: {
                            fontSize: 11,
                        },
                    },
                }}
            />
        </Box>
    );
}
